import { RichMediaShowType } from '@/const/richMedia';
import { OneOf } from '@/interface';
import { RichMediaValue } from '@/interface/media';

export enum Direction {
  up,
  down,
}

export enum SenderType {
  SENDER_TYPE_BOT = 1,
  SENDER_TYPE_SYSTEM,
  SENDER_TYPE_USER,
}

export enum ConversationType {
  C2C = 'C2C',
  GROUP = 'GROUP',
}

export enum EventType {
  EVENT_TYPE_MESSAGE = 1,
  EVENT_TYPE_ACTION,
}

export enum ContentType {
  CONTENT_TYPE_TEXT = 1,
  CONTENT_TYPE_IMAGE,
  CONTENT_TYPE_AUDIO,
  CONTENT_TYPE_VIdEO,
}

export enum ActionType {
  ACTION_TYPE_BEGINSCENE = 1,
  ACTION_TYPE_ENDSCENE,
  ACTION_TYPE_SHOWVOICEOVER,
  ACTION_TYPE_SHOWCARD,
  ACTION_TYPE_SHOWCLUE,
  ACTION_TYPE_CHANGEIMAGE,
  ACTION_TYPE_CHANGECLOSENESS,
  ACTION_TYPE_MISSIONCOMPLETED,
  ACTION_TYPE_CHANGECOUNTDOWN,
}

export enum ChangeMediaType {
  CHANGE_MEDIA_TYPE_BACKGROUND_IMAGE = 1,
  CHANGE_MEDIA_TYPE_FOREGROUND_IMAGE,
  CHANGE_MEDIA_TYPE_BGM_AUDIO,
  CHANGE_MEDIA_TYPE_FULL_VIDEO,
  CHANGE_MEDIA_TYPE_BACKGROUND_VIDEO = 22,
}

export enum ChangeMediaOperationType {
  CHANGE_MEDIA_OPERATION_TYPE_DEFAULT,
  CHANGE_MEDIA_OPERATION_TYPE_MODIFY,
  CHANGE_MEDIA_OPERATION_TYPE_REMOVE,
}

export enum CardType {
  CARDTYPE_STORYCARD = 1,
  CARDTYPE_ITEMSCARD,
}

export interface IOriConversation {
  conversationID: string;
  type: ConversationType;
  userProfile: {
    userID: string;
    avatar: string;
    nick: string;
  };
  lastMessage: {
    fromAccount: string;
    lastSequence: number;
    lastTime: number;
    payload: {
      data: string;
    };
  };
  unreadCount: number;
}

export type IConversation = Omit<IOriConversation, 'lastMessage'> & {
  lastMessage: {
    fromAccount: string;
    lastSequence: number;
    lastTime: number;
    payload: {
      data: IEvent;
    };
  };
};

export interface IFriend {
  userID: string;
  profile: {
    nick: string;
    avatar: string;
  };
}

export interface IRichMediaUrl {
  url: string;
  localPath: string;
}

export interface IOriIMMessage {
  ID: string;
  conversationID: string;
  conversationType: ConversationType;
  avatar: string;
  nick: string;
  to: string;
  from: string;
  flow: 'in' | 'out';
  payload: {
    data: string;
  };
}

export interface ISender {
  senderId: string;
  type: SenderType;
}

export interface ITextContent {
  text: string;
  showType: RichMediaShowType;
}

export interface IImageSample {
  primary: string;
  complementary: string;
}

export interface IImageContent {
  url: IRichMediaUrl;
  showType: RichMediaShowType;
  name: string;
  width: number;
  height: number;
  sample: IImageSample;
  promptText: string;
}

export interface IAudioContent {
  url: IRichMediaUrl;
  autoPlay: boolean;
  loopPlay: boolean;
  promptText: string;
}

export interface IVideoContent {
  url: IRichMediaUrl;
  showType: RichMediaShowType;
  autoPlay: boolean;
  promptText: string;
}

export interface IMessageContent {
  text: ITextContent;
  image: IImageContent;
  audio: IAudioContent;
  video: IVideoContent;
}

export type IMessage = {
  type: ContentType;
  sender: ISender;
} & OneOf<IMessageContent>;

export interface IBeginSceneAction {
  events: IEvent[];
  displayText: string;
}

export interface IEndSceneAction {
  events: IEvent[];
  nextSceneId: string;
  displayText: string;
  displayImage: IImageContent;
}

export type IShowVoiceoverAction = IBeginSceneAction;

export interface ICard {
  id: string;
  cardId: string;
  desc: string;
  bgImage: RichMediaValue;
  name: string;
  rarity: number;
  isHasCard: boolean;
}
export interface IShowCardAction {
  type: CardType;
  card: ICard | null;
  itemCard: ICard | null;
}

export interface IShowClueAction {
  clues: IEvent[];
  displayText: string;
}

interface IChangeMediaContent {
  backgroundImage?: RichMediaValue;
  foregroundImage?: RichMediaValue;
  bgmAudio?: RichMediaValue;
  fullVideo?: RichMediaValue;
  backgroundVideo?: IVideoContent;
}

export type IChangeMediaAction = {
  type: ChangeMediaType;
  operation?: ChangeMediaOperationType;
} & OneOf<IChangeMediaContent>;

export interface IChangeClosenessAction {
  closeness: number;
}

export enum AssetsType {
  ASSETS_TYPE_DEFAULT = 0,
  ASSETS_TYPE_PLOTCARD = 1,
  ASSETS_TYPE_ITEMSCARD = 2,
  ASSETS_TYPE_POINT = 3,
}
export interface ICardContent {
  itemsCard: ICard;
  plotCard: ICard;
}

export type IAsset = {
  id: string;
  type: AssetsType;
  events?: IEvent[];
} & OneOf<ICardContent>;

export interface IMissionCompletedAction {
  missionId: string;
  missionName: string;
  missionImage: IImageContent;
  lastText: string;
  assets: IAsset[];
}

export interface IChangeCountDownAction {
  timeDelta: number;
}

export interface IActionContent {
  beginScene?: IBeginSceneAction;
  endScene?: IEndSceneAction;
  showVoiceover?: IShowVoiceoverAction;
  showAsset?: IShowCardAction;
  showClue?: IShowClueAction;
  changeMedia?: IChangeMediaAction;
  changeCloseness?: IChangeClosenessAction;
  missionCompleted?: IMissionCompletedAction;
  changeCountDown?: IChangeCountDownAction;
}

export type IAction = {
  type: ActionType;
} & IActionContent;

export interface IEventContent {
  message: IMessage;
  action: IAction;
}

export type IEvent = {
  type: EventType;
  isFinish: boolean;
} & OneOf<IEventContent>;

export type IIMMessage = Omit<IOriIMMessage, 'payload'> & {
  payload: {
    data: IEvent;
  };
};

export const INIT_IMAGE_CONTENT: RichMediaValue = {
  url: {
    url: '', localPath: '', name: '占位图',
  },
  sample: { primary: '', complementary: '' },
  showType: RichMediaShowType.SHOW_TYPE_INLINE,
};
